import { coupsRecus, coupsRendusSurTrou } from '@golf/common';
import { useCallback, useEffect, useState } from 'react';

/* Lire une carte en BRUT ou en NET.
 *
 * ── Pourquoi ce choix est gardé ─────────────────────────────────────────────
 *
 * Un joueur lit ses cartes toujours de la même façon : celui qui joue au net y
 * reste, celui qui compte en brut aussi. Redemander à chaque écran serait
 * redemander une chose déjà dite — et la carte s'ouvre justement au moment où
 * l'on n'a qu'une main libre.
 *
 * Gardé dans le navigateur et non sur le serveur : c'est une façon de REGARDER,
 * pas une donnée du joueur. Elle n'a pas à voyager, ni à valoir sur le
 * téléphone de quelqu'un d'autre. Et si le stockage refuse — navigation privée,
 * réglage restrictif — l'écran retombe sur le brut sans broncher.
 */
const CLE = 'golf.carte.net';

function lire(): boolean {
    try {
        return localStorage.getItem(CLE) === '1';
    } catch {
        return false;
    }
}

/** Le mode de lecture des cartes, partagé par tous les écrans qui en montrent. */
export function useScoreNet(): [boolean, (v: boolean) => void] {
    const [net, setNet] = useState(lire);
    // Relu à l'ouverture : deux onglets, ou un retour sur l'application après
    // un changement fait ailleurs.
    useEffect(() => { setNet(lire()); }, []);
    const poser = useCallback((v: boolean) => {
        setNet(v);
        try {
            localStorage.setItem(CLE, v ? '1' : '0');
        } catch { /* le choix vaudra pour cet écran seulement */ }
    }, []);
    return [net, poser];
}

/** Ce qu'il faut d'un joueur pour savoir combien de coups il reçoit. */
export interface JoueurHandicape {
    handicapIndex?: number | null;
    slope?: number | null;
    courseRating?: number | null;
    /** Le plafond du handicap de jeu. La formule n'en connait pas ; l'usage en
     *  applique un, et il ne concerne que les index qui le dépassent. */
    maxHandicapStrokes?: number | null;
}

/** Ce qu'il faut d'un trou : son par, et sa place dans l'ordre de difficulté. */
export interface TrouCarnet {
    number: number;
    par: number | null;
    strokeIndex: number | null;
}

export interface Rendus {
    /** Les coups reçus sur ce parcours, depuis ce départ. `null` sans index. */
    total: number | null;
    /** Combien de coups sont rendus sur chaque trou, par numéro de trou. */
    parTrou: Map<number, number>;
    /** Le carnet donne-t-il l'ordre de difficulté ? Sans lui, on ne sait pas
     *  répartir, et l'écran doit le dire au lieu d'afficher un net inventé. */
    indexConnus: boolean;
    /** Le calcul est-il APPROCHÉ, faute de slope ou de SSS au carnet ? */
    approche: boolean;
    /** Le plafond a-t-il mordu ? L'écran le dit : un joueur à l'index 54 qui
     *  reçoit 36 coups doit savoir d'où vient le nombre. */
    plafonne: boolean;
}

/* Les coups rendus d'un joueur sur un parcours.
 *
 * Le par total vient du carnet et non d'une constante : un neuf trous ne fait
 * pas 72, et l'écart entre le SSS et le par est précisément ce qui corrige la
 * difficulté d'un parcours court.
 */
export function rendusDuJoueur(
    joueur: JoueurHandicape,
    trous: TrouCarnet[],
    /** Le plafond imposé par le PARCOURS, s'il en impose un. Il l'emporte sur
     *  celui du joueur : c'est le parcours qui édicte la règle chez lui, et un
     *  joueur qui fréquente deux clubs aux conventions différentes ne peut pas
     *  avoir raison avec un seul réglage. */
    plafondParcours?: number | null,
): Rendus {
    const plafond = plafondParcours ?? joueur.maxHandicapStrokes ?? null;
    const nbTrous = trous.length;
    const par = trous.reduce<number | null>(
        (t, h) => (h.par == null || t == null ? null : t + h.par),
        0,
    );
    const total = coupsRecus(
        joueur.handicapIndex,
        { slope: joueur.slope, courseRating: joueur.courseRating, par },
        nbTrous,
        plafond,
    );
    const parTrou = new Map<number, number>();
    for (const h of trous) {
        const n = coupsRendusSurTrou(total, h.strokeIndex, nbTrous);
        if (n != null) parTrou.set(h.number, n);
    }
    return {
        total,
        parTrou,
        indexConnus: nbTrous > 0 && trous.every((h) => h.strokeIndex != null),
        approche: joueur.slope == null || joueur.courseRating == null,
        plafonne: plafond != null && (joueur.handicapIndex ?? 0) > plafond,
    };
}
